import { defineStore } from 'pinia';
import { authApi, type UserProfile, type UserType, type QuotaUsage } from '@/api/auth';
import { startHeartbeat, stopHeartbeat } from '@/utils/heartbeat';

/** 注册用户默认区域配额（区县记录数上限；与后端 config/quota.json 保持一致） */
export const DEFAULT_REGION_QUOTA = 3;

/** 省级名称（所有注册用户均可查看完整机会帐，不占配额） */
export const PROVINCE_NAME = '广东省';

interface AuthState {
  loggedIn: boolean;
  phoneMasked: string;
  isAdmin: boolean;
  isSuperAdmin: boolean;
  /** 用户类型：super / admin / consultant / user */
  userType: UserType;
  /** 区域配额（区县记录数上限；-1 = 不限） */
  regionQuota: number;
  /** 已授予完整权限的区域（不含省级） */
  grantedRegions: string[];
  /** 配额占用明细 */
  quotaUsage: QuotaUsage;
  /** 区域配置是否已锁定（提交后不可自行变更） */
  locked: boolean;
  /** 从未配置过任何区域 → 登录后需引导配置 */
  needsRegionSetup: boolean;
  profile: UserProfile | null;
  pendingAction: 'open-account' | 'submit-contact' | null;
}

function emptyUsage(quota: number): QuotaUsage {
  return {
    cityCount: 0, cityCapacity: Math.floor(quota / 3), remainingCities: Math.floor(quota / 3),
    countyCount: 0, countyCapacity: quota, remainingCounties: quota,
    remainingQuota: quota
  };
}

export const useAuthStore = defineStore('auth', {
  state: (): AuthState => ({
    loggedIn: false,
    phoneMasked: '',
    isAdmin: false,
    isSuperAdmin: false,
    userType: 'user',
    regionQuota: DEFAULT_REGION_QUOTA,
    grantedRegions: [],
    quotaUsage: emptyUsage(DEFAULT_REGION_QUOTA),
    locked: false,
    needsRegionSetup: false,
    profile: null,
    pendingAction: null
  }),
  getters: {
    /** 登录后展示名：优先昵称，其次脱敏手机号 */
    displayName(state): string {
      return state.profile?.nickname?.trim() || state.phoneMasked || '';
    },
    /** 是否有昵称（用于区分是否展示手机号） */
    hasNickname(state): boolean {
      return Boolean(state.profile?.nickname?.trim());
    },
    /** 是否拥有全部区域（超管 / 管理员 / 咨询顾问） */
    hasAllRegions(state): boolean {
      return state.userType === 'super' || state.userType === 'admin' || state.userType === 'consultant';
    },
    /** 是否为咨询顾问 */
    isConsultant(state): boolean {
      return state.userType === 'consultant';
    },
    /** 省级机会帐是否可看完整（所有注册用户均可，不占配额） */
    canViewProvince(): boolean {
      return this.hasAllRegions || this.loggedIn;
    },
    /**
     * 空闲配额数（右上角小红点口径：只算剩余区县额度）
     * 管理员 / 顾问不显示
     */
    idleQuota(): number {
      if (this.hasAllRegions) return 0;
      return Math.max(0, this.quotaUsage?.remainingQuota ?? 0);
    },
    /** 是否有空闲配额（需要提醒用户去配置区域） */
    hasIdleQuota(): boolean {
      return !this.hasAllRegions && this.idleQuota > 0;
    },
    /** 是否从未配置过任何区域（老用户可能未选区域） */
    needsRegionConfig(): boolean {
      return !this.hasAllRegions && this.needsRegionSetup;
    },
    /**
     * 判断能否查看某区域的完整机会帐
     *  - 管理员 / 顾问 → 全部区域
     *  - 省级 → 所有注册用户均可
     *  - 市级 / 区县级 → 仅 grantedRegions 内的区域
     */
    canViewRegion(): (regionName: string) => boolean {
      return (regionName: string) => {
        if (this.hasAllRegions) return true;
        if (!regionName) return false;
        if (regionName === PROVINCE_NAME) return this.loggedIn;
        return this.grantedRegions.includes(regionName);
      };
    }
  },
  actions: {
    async fetchMe() {
      try {
        const me = await authApi.me();
        this.loggedIn = me.loggedIn;
        this.phoneMasked = me.phoneMasked;
        this.isAdmin = me.isAdmin;
        this.isSuperAdmin = me.isSuperAdmin ?? false;
        this.userType = me.userType ?? 'user';
        this.regionQuota = me.regionQuota ?? DEFAULT_REGION_QUOTA;
        this.grantedRegions = me.grantedRegions ?? [];
        this.quotaUsage = me.quotaUsage ?? emptyUsage(this.regionQuota);
        this.locked = me.locked ?? false;
        this.needsRegionSetup = me.needsRegionSetup ?? false;
        this.profile = me.profile;
        if (this.loggedIn) startHeartbeat();
      } catch { /* 未登录即保持 false */ }
    },
    async logout() {
      try { await authApi.logout(); } catch { /* ignore */ }
      this.loggedIn = false;
      this.phoneMasked = '';
      this.isAdmin = false;
      this.isSuperAdmin = false;
      this.userType = 'user';
      this.regionQuota = DEFAULT_REGION_QUOTA;
      this.grantedRegions = [];
      this.quotaUsage = emptyUsage(DEFAULT_REGION_QUOTA);
      this.locked = false;
      this.needsRegionSetup = false;
      this.profile = null;
      stopHeartbeat();
    },
    setPending(action: AuthState['pendingAction']) { this.pendingAction = action; },
    clearPending() { this.pendingAction = null; }
  }
});
